import React, { useEffect, useRef, useState, useCallback } from 'react';
import _ from 'lodash';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Tilt from 'react-parallax-tilt';
import Card from './Card';
import styles from '../styles/components/_HomeEventList.module.scss';
import { formatEventForCard, formatDateParts } from '../utils/formatters';
import Loader from './Loader';
import { createSlug } from '../utils/slugUtils';
import { useCustomNavigation } from './NavigationContext';

gsap.registerPlugin(ScrollTrigger);

const HomeEventList = ({ events = [], currentEventId, isEventPage = false }) => {
    const { customNavigate } = useCustomNavigation();
    const eventListRef = useRef(null);
    const cardsRef = useRef([]);
    const [titleText, setTitleText] = useState('');
    const [currentWeekEvents, setCurrentWeekEvents] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const animationsContext = useRef(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const setupAnimations = useCallback(() => {
        if (!eventListRef.current || !cardsRef.current.length) return;

        const cards = cardsRef.current.filter(Boolean);

        cards.forEach((card, i) => {
            gsap.set(card, {
                autoAlpha: 0,
                y: 0,
                rotate: 0
            });

            ScrollTrigger.create({
                trigger: card,
                start: "top bottom-=100",
                onEnter: () => {
                    gsap.to(card, {
                        autoAlpha: 1,
                        y: 50,
                        rotate: i % 2 === 0 ? 10 : -10,
                        duration: 1.2,
                        ease: "power3.out",
                        delay: i * 0.2
                    });
                }
            });

            const parallaxSpeed = (i % 3 + 1) * 10; // Réduction de la vitesse du parallax de 15 à 5
            gsap.to(card, {
                yPercent: -parallaxSpeed, // Changement du sens du déplacement (-parallaxSpeed à parallaxSpeed)
                ease: "none",
                scrollTrigger: {
                    trigger: card,
                    start: "top bottom+=100", // Ajustement du point de départ
                    end: "bottom top",
                    scrub: 0.5 // Réduction de la durée du scrub
                }
            });
        });
    }, []);

    const getTitleText = useCallback((weekStartDate) => {
        if (!weekStartDate) return '';
        const today = new Date();
        const weekStart = new Date(weekStartDate);
        today.setHours(0, 0, 0, 0);
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        if (isEventPage) {
            const isCurrentWeek = today >= weekStart && today <= weekEnd;
            return isCurrentWeek ? 'Cette semaine' : 'La même semaine';
        }

        if (today >= weekStart && today <= weekEnd) return 'Cette semaine';

        const weekStartFormatted = weekStart.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
        const weekEndFormatted = weekEnd.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
        return (
            <>
                <span className={styles.prefix}>Semaine</span>
                <span className={styles.dates}>Du {weekStartFormatted} au {weekEndFormatted}</span>
            </>
        );
    }, [isEventPage]);

    useEffect(() => {
        if (!events.length) return;

        const sortedEvents = [...events].sort((a, b) => new Date(a.date) - new Date(b.date));
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Trouver la semaine contenant aujourd'hui
        let weekStart = null;
        for (let event of sortedEvents) {
            const eventDate = new Date(event.date);
            const start = new Date(eventDate);
            start.setDate(eventDate.getDate() - eventDate.getDay() + 1);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            if (today >= start && today <= end) {
                weekStart = start;
                break;
            }
        }

        // Si aucune semaine ne contient aujourd'hui, prendre la prochaine semaine à venir
        if (!weekStart) {
            for (let event of sortedEvents) {
                const eventDate = new Date(event.date);
                const start = new Date(eventDate);
                start.setDate(eventDate.getDate() - eventDate.getDay() + 1);
                if (start > today) {
                    weekStart = start;
                    break;
                }
            }
        }

        // Si toujours rien, prendre la première semaine (fallback)
        if (!weekStart && sortedEvents.length) {
            const eventDate = new Date(sortedEvents[0].date);
            weekStart = new Date(eventDate);
            weekStart.setDate(eventDate.getDate() - eventDate.getDay() + 1);
        }

        setTitleText(getTitleText(weekStart));

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekEvents = sortedEvents.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= weekStart && eventDate <= weekEnd;
        });

        setCurrentWeekEvents(weekEvents);
        setIsLoaded(true);
    }, [events, getTitleText]);

    useEffect(() => {
        if (!isLoaded || !currentWeekEvents.length) return;

        const timer = setTimeout(() => {
            const initAnimations = () => {
                if (animationsContext.current) {
                    animationsContext.current.revert();
                }

                animationsContext.current = gsap.context(() => {
                    ScrollTrigger.getAll()
                        .filter(st => st.vars.trigger?.closest('.homeEventList'))
                        .forEach(st => st.kill());

                    setupAnimations();
                }, eventListRef.current);

                requestAnimationFrame(() => {
                    ScrollTrigger.refresh(true);
                });
            };

            initAnimations();

            const handleResize = _.debounce(() => {
                initAnimations();
            }, 250);

            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("resize", handleResize);
                if (animationsContext.current) {
                    animationsContext.current.revert();
                }
            };
        }, 500);

        return () => clearTimeout(timer);
    }, [isLoaded, currentWeekEvents, setupAnimations]);

    const handleCardClick = (event) => {
        if (!event || !event.title) {
            console.warn("Données manquantes pour l'événement", event);
            return;
        }

        const eventTitle = typeof event.title === 'object' ? event.title.rendered : event.title;
        const slug = createSlug(eventTitle, event);

        if (!slug) {
            console.warn("Impossible de créer un slug valide pour l'événement:", eventTitle);
            return;
        }

        customNavigate(`/programmation/${slug}`, {
            state: { eventData: event },
            replace: true
        });
    };

    if (!events.length) return <Loader type="rings" />;

    return (
        <div className={styles.homeEventList}>
            <div className={styles.leftPanel}>
                <h1>{titleText}</h1>
            </div>
            <div className={styles.eventList} ref={eventListRef}>
                {currentWeekEvents.map((event, index) => {
                    const formattedEvent = formatEventForCard(event);
                    const dateParts = formatDateParts(formattedEvent.date, formattedEvent.dateFin);

                    return (
                        <Tilt
                            tiltMaxAngleX={!isMobile ? 8 : 0}
                            tiltMaxAngleY={!isMobile ? 8 : 0}
                            perspective={1000}
                            scale={1.05}
                            transitionSpeed={2000}
                            gyroscope={!isMobile}
                            key={event.id || index}
                            className={styles.eventCardWrapper}
                            style={{
                                cursor: 'pointer',
                                userSelect: 'none'
                            }}
                            onClick={() => handleCardClick(event)}
                        >
                            <div
                                ref={(el) => (cardsRef.current[index] = el)}
                                style={{
                                    willChange: 'transform',
                                    transformStyle: 'preserve-3d',
                                    filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
                                    transition: 'filter 0.5s ease'
                                }}
                                className={styles.cardContainer}
                            >
                                <Card
                                    mainImage={formattedEvent.mainImage}
                                    title={formattedEvent.title}
                                    dateParts={dateParts}
                                    description={formattedEvent.description}
                                    orientation="vertical"
                                    height="auto"
                                    typePerformance={formattedEvent.typePerformance}
                                    subtitle={formattedEvent.subtitle}
                                    imagePosition="left"
                                    tags={formattedEvent.type ? [formattedEvent.type] : []}
                                    showOverlay={true}
                                    onClick={() => handleCardClick(event)}
                                    linkTo={`/agenda/${createSlug(event.title)}`}
                                    customNavigate={customNavigate}
                                    additionalArtists={formattedEvent.additionalArtists || []}
                                    addInfosEvent={formattedEvent.addInfosEvent}
                                />
                            </div>
                        </Tilt>
                    );
                })}
            </div>
            <button
                className="custom-button custom-button--light"
                onClick={() => customNavigate('/programmation')}
            >
                programmation complète
            </button>
        </div>
    );
};

export default HomeEventList;